import { createFetch } from '@vueuse/core'
import { destr } from 'destr'
import type { UserData } from '@/stores/types'
import { useUserStore } from '@/stores/userStore'
import { formatUserFunctionalityRules } from '@/utils/helpers/formatUserFunctionalityRules'

export const useApiModified = async (
  urlAndOptions: [ComputedRef<string>, RequestInit?],
  ability?: any,
) => {
  const fetchInstance = createFetch({
    baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
    fetchOptions: {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    },
    options: {
      refetch: true,
      async beforeFetch({ options }) {
        const accessToken = useCookie('accessToken').value

        if (accessToken) {
          options.headers = {
            ...options.headers,
            Authorization: `Bearer ${accessToken}`,
          }
        }

        return { options }
      },
      afterFetch(ctx) {
        if (ability) {
          const store = useUserStore()

          store.userData = useCookie<UserData>('userData').value

          const userFunctionalityRules = formatUserFunctionalityRules()

          ability.update(userFunctionalityRules)
        }

        const { data, response } = ctx

        let parsedData = null
        try {
          parsedData = destr(data)
        } catch (error) {
          console.error(error)
        }

        return { data: parsedData, response }
      },
      async onFetchError(ctx) {
        const { data, response } = ctx

        let parsedData
        try {
          parsedData = destr(data)

          if (
            response?.status === 400 &&
            typeof parsedData === 'object' &&
            parsedData !== null &&
            'message' in parsedData &&
            typeof parsedData.message === 'object' &&
            parsedData.message !== null &&
            'message' in parsedData.message
          )
            parsedData.message = parsedData.message.message

          if (response?.status === 403 || response?.status === 401) {
            const url = (parsedData as { url?: string })?.url
            if (url) window.location.href = url
          }
        } catch (error) {
          console.error(error)
        }

        return { error: parsedData, response }
      },
    },
  })

  const [url, requestInit] = urlAndOptions
  const fetchResult = await fetchInstance(url, requestInit || {})

  return {
    data: fetchResult.data,
    response: fetchResult.response,
    error: fetchResult.error,
    execute: fetchResult.execute,
  }
}
